if (!customElements.get('the-breakdown')) {
  customElements.define(
    'the-breakdown',
    class TheBreakdown extends HTMLElement {
      connectedCallback() {
        this.hotspots = Array.from(this.querySelectorAll('.the-breakdown__hotspot:not([data-sample-hotspot])'));
        this.isDesignMode = this.dataset.designMode === 'true';

        if (this.isDesignMode) {
          this.initDesignMode();
          return;
        }

        if (!this.hotspots.length) return;

        this.onHotspotClick = this.handleHotspotClick.bind(this);
        this.onMediaClick = this.handleMediaClick.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onKeyDown = this.handleKeyDown.bind(this);

        this.hotspots.forEach((hotspot) => {
          hotspot.addEventListener('click', this.onHotspotClick);
        });

        this.addEventListener('click', this.onMediaClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeyDown);
      }

      disconnectedCallback() {
        this.hotspots?.forEach((hotspot) => {
          hotspot.removeEventListener('click', this.onHotspotClick);
          this.removeDragListeners(hotspot);
        });
        this.removeEventListener('click', this.onMediaClick);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeyDown);
      }

      initDesignMode() {
        this.classList.add('the-breakdown__media--design-mode');

        this.hotspots.forEach((hotspot) => {
          hotspot.classList.add('the-breakdown__hotspot--draggable');
          hotspot.setAttribute('aria-expanded', 'false');
          this.ensureCoordLabel(hotspot);
          this.updateHotspotPosition(hotspot);
          this.bindDrag(hotspot);
        });
      }

      ensureCoordLabel(hotspot) {
        if (hotspot.querySelector('.the-breakdown__hotspot-coords')) return;

        const label = document.createElement('span');
        label.className = 'the-breakdown__hotspot-coords';
        label.setAttribute('aria-hidden', 'true');
        hotspot.appendChild(label);
      }

      bindDrag(hotspot) {
        const onPointerDown = (event) => {
          if (event.button !== undefined && event.button !== 0) return;

          event.preventDefault();
          event.stopPropagation();

          hotspot.classList.add('is-dragging');
          hotspot.setPointerCapture?.(event.pointerId);

          const onPointerMove = (moveEvent) => {
            this.setHotspotFromPointer(hotspot, moveEvent.clientX, moveEvent.clientY);
          };

          const onPointerUp = () => {
            hotspot.classList.remove('is-dragging');
            hotspot.releasePointerCapture?.(event.pointerId);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
          };

          window.addEventListener('pointermove', onPointerMove);
          window.addEventListener('pointerup', onPointerUp);

          this.setHotspotFromPointer(hotspot, event.clientX, event.clientY);
        };

        hotspot.addEventListener('pointerdown', onPointerDown);
        hotspot._breakdownPointerDown = onPointerDown;
      }

      removeDragListeners(hotspot) {
        if (hotspot._breakdownPointerDown) {
          hotspot.removeEventListener('pointerdown', hotspot._breakdownPointerDown);
        }
      }

      setHotspotFromPointer(hotspot, clientX, clientY) {
        const rect = this.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
        const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));

        hotspot.style.setProperty('--hotspot-x', `${x.toFixed(1)}%`);
        hotspot.style.setProperty('--hotspot-y', `${y.toFixed(1)}%`);
        hotspot.dataset.positionX = Math.round(x).toString();
        hotspot.dataset.positionY = Math.round(y).toString();

        this.updateHotspotPosition(hotspot);
      }

      updateHotspotPosition(hotspot) {
        const x = hotspot.dataset.positionX || this.readPercent(hotspot, '--hotspot-x');
        const y = hotspot.dataset.positionY || this.readPercent(hotspot, '--hotspot-y');
        const label = hotspot.querySelector('.the-breakdown__hotspot-coords');

        if (label) {
          label.textContent = `X ${x} · Y ${y}`;
        }
      }

      readPercent(hotspot, variable) {
        const value = hotspot.style.getPropertyValue(variable) || getComputedStyle(hotspot).getPropertyValue(variable);
        return Math.round(parseFloat(value));
      }

      handleHotspotClick(event) {
        event.stopPropagation();
        const hotspot = event.currentTarget;
        const wasActive = hotspot.classList.contains('is-active');

        this.closeAll();

        if (!wasActive) {
          hotspot.classList.add('is-active');
          hotspot.setAttribute('aria-expanded', 'true');
        }
      }

      handleMediaClick(event) {
        if (event.target.closest('.the-breakdown__hotspot')) return;
        this.closeAll();
      }

      handleDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.closeAll();
      }

      handleKeyDown(event) {
        if (event.key !== 'Escape') return;
        this.closeAll();
      }

      closeAll() {
        this.hotspots.forEach((hotspot) => {
          hotspot.classList.remove('is-active');
          hotspot.setAttribute('aria-expanded', 'false');
        });
      }
    }
  );
}
